import axios from 'axios';

/**
 * The signed-in staff member's session, attached to every admin API call.
 *
 * The backend refuses staff-only routes without a staff token. Most pages call
 * them with plain `axios` or `fetch` and no headers, so this adds the header in
 * one place instead of in a hundred.
 *
 * Only requests to our own API's `/v1/admin/` routes get it, and never when the
 * caller already set Authorization: payment pages send a manager's one-off
 * approval that way, and that must win.
 */

const BACKEND = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const STAFF_KEYS = {
  session: ['token', 'adminData'],
  local: ['operatorToken', 'refreshToken'],
};

export const getStaffToken = (): string =>
  sessionStorage.getItem('token') || localStorage.getItem('operatorToken') || '';

/** Sign out on this device, wherever the session was stored. */
export const clearStaffSession = () => {
  STAFF_KEYS.session.forEach((key) => sessionStorage.removeItem(key));
  STAFF_KEYS.local.forEach((key) => localStorage.removeItem(key));
};

const isAdminApi = (url: string) => {
  if (!url) return false;
  const absolute = url.startsWith('http') ? url : `${BACKEND}${url.startsWith('/') ? '' : '/'}${url}`;
  return absolute.startsWith(`${BACKEND}/v1/admin/`);
};

let installed = false;

export const installStaffAuth = () => {
  if (installed) return;
  installed = true;

  axios.interceptors.request.use((config) => {
    const token = getStaffToken();
    const url = `${config.baseURL ?? ''}${config.url ?? ''}`;
    if (token && isAdminApi(url)) {
      const headers: any = config.headers ?? {};
      const existing = typeof headers.get === 'function' ? headers.get('Authorization') : headers.Authorization;
      if (!existing) {
        if (typeof headers.set === 'function') headers.set('Authorization', `Bearer ${token}`);
        else headers.Authorization = `Bearer ${token}`;
        config.headers = headers;
      }
    }
    return config;
  });

  const originalFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const token = getStaffToken();
    if (!token || !isAdminApi(url)) return originalFetch(input, init);

    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    if (!headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
    return originalFetch(input, { ...init, headers });
  };
};
